import AppointmentTile from "@components/Booking/DoubleBooking/AppointmentTile";
import {AsyncButton} from "@components/Button";
import {ButtonStatus} from "@components/Button/constants";
import {useTranslation} from "@i18n/client";
import React, {useCallback, useState} from "react";

import {I18nNamespace} from "@i18n";
import {DuplicateAppointment} from "@services/graphql/queries/appointment";

export type Props = {
  existingAppointment: Omit<DuplicateAppointment, "id">;
  attemptingToBook: Omit<DuplicateAppointment, "id">;
  loading: boolean;
  onConfirmExisting: () => unknown;
  onReschedule: () => unknown;
};

const AppointmentSelector = ({
  existingAppointment,
  attemptingToBook,
  loading,
  onConfirmExisting,
  onReschedule,
}: Props) => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  // true = reschedule on submit. false = keep old on submit. null = nothing selected yet, submission disabled.
  const [reschedule, setReschedule] = useState<boolean | null>(null);

  const onChooseExisting = useCallback(() => setReschedule(false), []);
  const onChooseNew = useCallback(() => setReschedule(true), []);

  const buttonLabel =
    reschedule === null
      ? i18n.t("Choose appointment above", {ns: "web-scheduling"})
      : reschedule
      ? i18n.t("Reschedule my appointment", {ns: "web-scheduling"})
      : i18n.t("Keep my existing appointment", {ns: "web-scheduling"});
  const loadingLabel = reschedule
    ? i18n.t("Rescheduling", {ns: "web-scheduling"})
    : i18n.t("Confirming", {ns: "web-scheduling"});
  const successLabel = reschedule
    ? i18n.t("Appointment Rescheduled")
    : i18n.t("Appointment Confirmed");

  return (
    <div className="flex flex-col w-[343px] justify-center gap-6">
      <div className="flex flex-row justify-between">
        <AppointmentTile
          thisAppt={existingAppointment}
          otherAppt={attemptingToBook}
          isExisting={true}
          onClick={onChooseExisting}
          isSelected={reschedule === false}
        />
        <AppointmentTile
          thisAppt={attemptingToBook}
          otherAppt={existingAppointment}
          isExisting={false}
          onClick={onChooseNew}
          isSelected={reschedule === true}
        />
      </div>
      <AsyncButton
        label={buttonLabel}
        loadingLabel={loadingLabel}
        successLabel={successLabel}
        disabled={reschedule === null}
        statusOverride={loading ? ButtonStatus.LOADING : undefined}
        onClick={reschedule ? onReschedule : onConfirmExisting}
      />
    </div>
  );
};
export default React.memo(AppointmentSelector);
